import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FileUpload,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Separator,
} from "@/components";
import { Combobox } from "@/components/ui/combobox";
import { FloatingLabelInput } from "@/components/ui/floating-label-input";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";
import { formatCpfCnpj } from "@/core/utils";

import {
  buildSubPartnerFormData,
  buildSubPartnerUpdateFormData,
  defaultOrganizationFormValues,
  mapSubPartnerApiToFormValues,
} from "../../projects/organization-register.hooks";
import {
  organizationRegisterFormSchema,
  TOrganizationRegisterFormData,
} from "../../projects/organization-register.schema";
import * as clientsApi from "../clients.api";
import { TClientExtendedResponse } from "../clients.types";

export const SubPartnerRegisterPage: React.FC = () => {
  const { clientId, subId } = useParams<{ clientId: string; subId?: string }>();
  const isEditMode = !!subId;
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: parentOrg } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId],
    queryFn: () => clientsApi.getClientById(clientId!),
    enabled: !!clientId,
    staleTime: 60_000,
  });

  // Carrega dados do sub-parceiro no modo edição
  const { data: subPartnerData, isLoading: isLoadingSubPartner } =
    useQuery<TClientExtendedResponse>({
      queryKey: ["sub-partner", subId],
      queryFn: () => clientsApi.getClientById(subId!),
      enabled: isEditMode && !!subId,
      staleTime: 60_000,
    });

  const editFormValues = React.useMemo(() => {
    if (!isEditMode || !subPartnerData) return undefined;
    return mapSubPartnerApiToFormValues(subPartnerData);
  }, [isEditMode, subPartnerData]);

  const form = useForm<TOrganizationRegisterFormData>({
    resolver: zodResolver(organizationRegisterFormSchema) as any,
    defaultValues: defaultOrganizationFormValues,
  });

  React.useEffect(() => {
    if (!editFormValues) return;
    form.reset(editFormValues, { keepDefaultValues: false });
    // Garante o CPF da org após o campo condicional montar (PF).
    if (editFormValues.document_type === "cpf" && editFormValues.cpf) {
      queueMicrotask(() => {
        form.setValue("cpf", editFormValues.cpf ?? "", {
          shouldDirty: false,
          shouldTouch: false,
        });
      });
    }
  }, [editFormValues, form]);

  const documentType = form.watch("document_type");

  const createMutation = useMutation({
    mutationFn: async (data: TOrganizationRegisterFormData) => {
      const formData = buildSubPartnerFormData(data);
      return clientsApi.createSubPartner(clientId!, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-partners", clientId] });
      toast({ title: "Parceiro cadastrado com sucesso!" });
      navigate(`/clients/${clientId}/sub-partners`);
    },
    onError: (error: Error & { field?: string }) => {
      if (error.field) {
        form.setError(error.field as any, { message: error.message });
      }
      toast({
        title: "Erro ao cadastrar parceiro",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: TOrganizationRegisterFormData) => {
      const formData = buildSubPartnerUpdateFormData(
        data,
        (subPartnerData as { opening_date?: string | Date })?.opening_date,
      );
      return clientsApi.updateSubPartnerPublic(subId!, formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-partners", clientId] });
      queryClient.invalidateQueries({ queryKey: ["sub-partner", subId] });
      toast({ title: "Parceiro atualizado com sucesso!" });
      navigate(`/clients/${clientId}/sub-partners`);
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar parceiro",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const activeMutation = isEditMode ? updateMutation : createMutation;

  // Verifica duplicidade (dentro do parceiro pai) ao sair do campo; só no cadastro.
  const checkDocumentOnBlur = React.useCallback(
    async (
      fieldName: "cnpj" | "cpf" | "company_representative.cpf",
      type: "cnpj" | "cpf" | "representative_cpf",
      rawValue: string
    ) => {
      const digits = rawValue.replace(/\D/g, "");
      if (isEditMode || !clientId || digits.length !== (type === "cnpj" ? 14 : 11)) return;
      try {
        const { exists, message } = await clientsApi.checkSubPartnerDocument(clientId, type, digits);
        if (exists) form.setError(fieldName, { message });
      } catch {
        // validação definitiva ocorre no submit
      }
    },
    [clientId, isEditMode, form]
  );

  const handleAddressBlur = React.useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      const zipcode = event.target.value;
      if (!zipcode) return;
      try {
        const addressData = await clientsApi.fetchZipCode(zipcode);
        if (addressData) {
          form.setValue("address.state", addressData.state_acronym);
          form.setValue("address.city", addressData.city_name);
          form.setValue("address.neighborhood", addressData.neighborhood_name);
          form.setValue("address.street", addressData.street_full);
        }
      } catch {
        // ignore
      }
    },
    [form]
  );

  const businessClassificationOptions = [
    { value: "comercio", label: t("clients.business_classification.comercial") },
    { value: "industria", label: t("clients.business_classification.industria") },
    { value: "servicos", label: t("clients.business_classification.servicos") },
  ];

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mx-auto max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/clients/${clientId}/sub-partners`)}
          className="p-3 hover:bg-muted/50"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditMode ? "Editar Parceiro" : "Cadastrar Parceiro"}
          </h1>
          {parentOrg?.name && (
            <p className="text-sm text-muted-foreground">
              Parceiro vinculado a <span className="font-medium">{parentOrg.name}</span>
            </p>
          )}
        </div>
      </div>

      {isEditMode && (isLoadingSubPartner || !editFormValues) ? (
        <div className="text-muted-foreground p-8 text-center">Carregando dados do parceiro…</div>
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>Dados do Parceiro</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => activeMutation.mutateAsync(data))}
              className="space-y-8"
            >
              {/* Toggle PF / PJ */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground">Tipo de pessoa:</span>
                <FormField
                  control={form.control}
                  name="document_type"
                  render={({ field }) => (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value === "cnpj" ? "default" : "outline"}
                        onClick={() => {
                          field.onChange("cnpj");
                          form.setValue("cpf", "");
                        }}
                      >
                        Pessoa Jurídica (CNPJ)
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={field.value === "cpf" ? "default" : "outline"}
                        onClick={() => {
                          field.onChange("cpf");
                          form.setValue("cnpj", "");
                          form.setValue("corporate_name", "");
                        }}
                      >
                        Pessoa Física (CPF)
                      </Button>
                    </div>
                  )}
                />
              </div>

              <Separator />

              {/* Dados da Empresa / Pessoa */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold">
                  {documentType === "cpf" ? "Dados da Pessoa" : t("clients.form.sections.companyData")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${documentType === "cpf" ? "Nome completo" : t("clients.form.fields.name")} *`}
                            placeholder=" "
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Mantém CPF e CNPJ registrados no form (evita valor perdido no toggle/reset). */}
                  <div className={documentType === "cnpj" ? undefined : "hidden"}>
                    <FormField
                      control={form.control}
                      name="cnpj"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={`${t("clients.form.fields.cnpj")} *`}
                              placeholder=" "
                              {...field}
                              onBlur={(e) => {
                                field.onBlur();
                                checkDocumentOnBlur("cnpj", "cnpj", e.target.value);
                              }}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                e.target.value = formatCpfCnpj(e.target.value);
                                field.onChange(e);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className={documentType === "cpf" ? undefined : "hidden"}>
                    <FormField
                      control={form.control}
                      name="cpf"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={`${t("clients.form.fields.cpf")} *`}
                              placeholder=" "
                              {...field}
                              onBlur={(e) => {
                                field.onBlur();
                                checkDocumentOnBlur("cpf", "cpf", e.target.value);
                              }}
                              value={field.value ?? ""}
                              onChange={(e) => {
                                e.target.value = formatCpfCnpj(e.target.value);
                                field.onChange(e);
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {documentType === "cnpj" && (
                    <FormField
                      control={form.control}
                      name="corporate_name"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={`${t("clients.form.fields.corporate_name")} *`}
                              placeholder=" "
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <FloatingLabelInput
                            label={`${t("clients.form.fields.email")} *`}
                            placeholder=" "
                            type="email"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="business_classification"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Combobox
                            label={`${t("clients.form.fields.business_classification")} *`}
                            placeholder={t("clients.form.fields.business_classification")}
                            options={businessClassificationOptions}
                            value={field.value || undefined}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              {/* Endereço */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold">{t("clients.form.sections.address")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(
                    [
                      { name: "address.postal_code", label: t("clients.form.fields.postal_code"), required: true, onBlur: handleAddressBlur },
                      { name: "address.street", label: t("clients.form.fields.street"), required: true },
                      { name: "address.number", label: t("clients.form.fields.number"), required: true },
                      { name: "address.complement", label: t("clients.form.fields.complement"), required: false },
                      { name: "address.neighborhood", label: t("clients.form.fields.neighborhood"), required: true },
                      { name: "address.city", label: t("clients.form.fields.city"), required: true },
                      { name: "address.state", label: t("clients.form.fields.state"), required: true },
                    ] as const
                  ).map((item) => (
                    <FormField
                      key={item.name}
                      control={form.control}
                      name={item.name}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={item.required ? `${item.label} *` : item.label}
                              placeholder=" "
                              {...field}
                              onBlur={(item as any).onBlur ?? field.onBlur}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>

              <Separator />

              {/* Representante */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold">
                  {documentType === "cpf" ? "Dados Pessoais" : t("clients.form.sections.representative")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(
                    [
                      { name: "company_representative.first_name", label: t("clients.form.fields.first_name") },
                      { name: "company_representative.last_name", label: t("clients.form.fields.last_name") },
                      { name: "company_representative.personal_email", label: t("clients.form.fields.personal_email"), type: "email" },
                      { name: "company_representative.cpf", label: t("clients.form.fields.cpf"), format: true },
                      { name: "company_representative.mobile_phone", label: t("clients.form.fields.mobile_phone") },
                    ] as const
                  ).map((item) => (
                    <FormField
                      key={item.name}
                      control={form.control}
                      name={item.name}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={`${item.label} *`}
                              placeholder=" "
                              type={(item as any).type ?? "text"}
                              {...field}
                              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                field.onBlur();
                                if (item.name === "company_representative.cpf") {
                                  checkDocumentOnBlur(item.name, "representative_cpf", e.target.value);
                                }
                              }}
                              onChange={
                                (item as any).format
                                  ? (e: React.ChangeEvent<HTMLInputElement>) => {
                                      e.target.value = formatCpfCnpj(e.target.value);
                                      field.onChange(e);
                                    }
                                  : field.onChange
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>

              <Separator />

              {/* Dados Bancários */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold">{t("clients.form.sections.bankAccount")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {(
                    [
                      { name: "bank_account.bank_name", label: t("clients.form.fields.bank_name") },
                      { name: "bank_account.bank_branch", label: t("clients.form.fields.bank_branch") },
                      { name: "bank_account.checking_account", label: t("clients.form.fields.checking_account") },
                      { name: "bank_account.checking_account_digit", label: t("clients.form.fields.checking_account_digit") },
                    ] as const
                  ).map(({ name, label }) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <FloatingLabelInput
                              label={`${label} *`}
                              placeholder=" "
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>

              <Separator />

              {/* Documentos */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold">{t("clients.form.sections.documents")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="document.company_logo"
                    render={({ field }) => (
                      <FormItem className="min-w-0">
                        <FormControl>
                          <FileUpload
                            label={t("clients.form.fields.company_logo")}
                            description={t("clients.form.fields.company_logo_description")}
                            accept="image/*,.pdf"
                            value={field.value ?? null}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {documentType === "cnpj" && (
                    <FormField
                      control={form.control}
                      name="document.cnpj_card"
                      render={({ field }) => (
                        <FormItem className="min-w-0">
                          <FormControl>
                            <FileUpload
                              label={t("clients.form.fields.cnpj_card")}
                              description={t("clients.form.fields.cnpj_card_description")}
                              accept=".pdf,.jpg,.jpeg,.png"
                              value={field.value ?? null}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              name={field.name}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="document.contract_signed"
                    render={({ field }) => (
                      <FormItem className="min-w-0 md:col-span-2">
                        <FormControl>
                          <FileUpload
                            label={t("clients.form.fields.signed_contract")}
                            description={t("clients.form.fields.signed_contract_description")}
                            accept=".pdf,.jpg,.jpeg,.png"
                            value={field.value || []}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            multiple
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/clients/${clientId}/sub-partners`)}
                  disabled={activeMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button type="submit" disabled={activeMutation.isPending} className="min-w-36">
                  {activeMutation.isPending
                    ? isEditMode ? "Salvando..." : "Cadastrando..."
                    : isEditMode ? "Salvar Alterações" : "Cadastrar Parceiro"}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
      )}
      </div>
    </div>
  );
};
